import endOfDay from 'date-fns/endOfDay';
import moment from 'moment';
import { createSelector } from 'reselect';
import { PricelistDto, ResourceType, UserGroupPermissionDto } from '../../../common/PricePrgClient';
import { RootState } from '../../../store';
import { notNull, getPermissions } from '../../../utils';
import { getCustomers } from '../../customer/redux/customerSelectors';
import { getAvailableResources, getCurrentUserPermissions } from '../../user/redux/userSelectors';
import { getSelectedUserGroup, getUserGroupPermissions } from '../../usergroup/redux/userGroupSelectors';

export const getPricelistDialogState = (state: RootState) => state.pricelist.ui.createPricelistDialog;

export const getSelectedPricelistIds = (state: RootState) => state.pricelist.selectedPricelistIds;
export const getUnfilteredPricelists = (state: RootState) => state.pricelist.pricelists;
export const getPricelistPeriods = (state: RootState) => state.pricelist.pricelistPeriods;
export const getSelectedPricelistCustomersIds = (state: RootState) => state.pricelist.pricelistCustomerIds;

export const getSelectedPricelistCustomers = createSelector(
    [getCustomers, getSelectedPricelistCustomersIds],
    (customers, selectedPricelistCustomerIds) =>
        selectedPricelistCustomerIds.map(custId => customers[custId]).filter(notNull)
);

export const getPricelists = createSelector(
    [getUnfilteredPricelists, getCurrentUserPermissions],
    (unfilteredPricelists, userPermissions) => {
        if (userPermissions.isAdmin) {
            return unfilteredPricelists;
        }

        const pricelists: { [pricelistId: number]: PricelistDto } = {};

        const pricelistPermissions = userPermissions.recordPermissions.find(
            x => x.resourceType === ResourceType.Pricelist
        );

        // Zero is a special record id which applies to all records
        if (pricelistPermissions?.recordPermissions.find(x => x.recordId === 0)?.canCreate) {
            return unfilteredPricelists;
        }

        if (pricelistPermissions != null) {
            for (const plp of pricelistPermissions.recordPermissions) {
                if (plp.recordId != null && plp.canRead) {
                    const pricelist = unfilteredPricelists[plp.recordId];
                    if (pricelist != null) {
                        pricelists[pricelist.pricelistId] = pricelist;
                    }
                }
            }
        }

        return pricelists;
    }
);

export const getSelectedPricelists = createSelector(
    [getSelectedPricelistIds, getPricelists],
    (selectedPricelistIds, pricelists) => selectedPricelistIds.map(plId => pricelists[plId]).filter(notNull)
);

export const getPricelistTableGroups = createSelector(
    [getSelectedPricelistIds, getPricelists],
    (selectedPricelistIds, pricelists) =>
        selectedPricelistIds
            .map(plId => pricelists[plId])
            .filter(notNull)
            .map(pl => ({
                id: pl.pricelistId,
                title: pl.name,
                isBasePricelist: pl.isBasePricelist
            }))
);

export const getPricelistPeriodTableItems = createSelector(
    [getSelectedPricelistIds, getPricelists, getPricelistPeriods],
    (selectedPricelistIds, pricelists, pricelistPeriods) =>
        selectedPricelistIds
            .map(plId => pricelists[plId])
            .filter(notNull)
            .flatMap(pl =>
                Object.values(pricelistPeriods)
                    .filter(plp => plp.pricelistId === pl.pricelistId)
                    .sort((a, b) => new Date(a.validFrom).getTime() - new Date(b.validFrom).getTime())
                    .map((plp, idx) => ({
                        id: plp.pricelistPeriodId,
                        group: plp.pricelistId,
                        startTime: moment(plp.validFrom),
                        endTime: moment(endOfDay(new Date(plp.validTo!))),
                        title: plp.priceAmount,
                        selectedBgColor: 'rgba(45,165,76,1)',
                        bgColor: idx % 2 === 0 ? 'rgb(33, 150, 243)' : 'rgba(93,178,247,1)',
                        selectedBorder: '1px solid rgba(39, 86, 42, 1)',
                        border: `1px solid ${idx % 2 === 0 ? 'rgb(26, 111, 179)' : 'rgb(26, 111, 179)'}`
                    }))
            )
);

export const getPricelistPermissionsFromSelectedGroup = createSelector(
    [getSelectedUserGroup, getUserGroupPermissions, getAvailableResources, getPricelists],
    (selectedUserGroup, userGroupPermissions, availableResources, pricelists) => {
        if (selectedUserGroup == null) {
            return [];
        }

        const recordResources = availableResources.recordResources;

        const pricelistPermissions = selectedUserGroup.permissions
            .map(permissionId => {
                const permission = userGroupPermissions[permissionId];
                if (
                    permission == null ||
                    permission.resource !== ResourceType.Pricelist ||
                    permission.recordId == null
                ) {
                    return null;
                }

                if (recordResources[permission.resource] == null) {
                    return null;
                }

                // 0 mean pricelists not created yet, e.g. permission for creating new pricelists
                if (permission.recordId === 0) {
                    return [0, permission] as const;
                }

                const pricelist = pricelists[permission.recordId];
                if (pricelist == null) {
                    return null;
                }

                return [pricelist.pricelistId, permission] as const;
            })
            .filter(notNull);

        const pricelistGroupPermissionObj: { [pricelistId: number]: UserGroupPermissionDto } = {};
        for (const [pricelistId, permission] of pricelistPermissions) {
            pricelistGroupPermissionObj[pricelistId] = permission;
        }

        return pricelistGroupPermissionObj;
    }
);

export const getSelectedPricelistPermissions = createSelector(
    [getSelectedPricelistIds, getCurrentUserPermissions],
    (selectedPricelistIds, userPermissions) =>
        getPermissions(ResourceType.Pricelist, selectedPricelistIds, userPermissions)
);
